export interface LoomPchipTable {
  readonly derivatives: readonly number[];
  readonly values: readonly number[];
  readonly widths: readonly number[];
  readonly xs: readonly number[];
}

function endpoint(firstWidth: number, secondWidth: number,
  firstSlope: number, secondSlope: number): number {
  let derivative = ((2 * firstWidth + secondWidth) * firstSlope
    - firstWidth * secondSlope) / (firstWidth + secondWidth);
  if (Math.sign(derivative) !== Math.sign(firstSlope)) derivative = 0;
  else if (Math.sign(firstSlope) !== Math.sign(secondSlope)
      && Math.abs(derivative) > 3 * Math.abs(firstSlope)) derivative = 3 * firstSlope;
  return derivative;
}

export function prepareLoomPchip(
  xs: readonly number[],
  values: readonly number[],
): LoomPchipTable {
  if (xs.length !== values.length || xs.length < 2
      || xs.some((value, index) => !Number.isFinite(value)
        || (index > 0 && value <= xs[index - 1]!))
      || values.some((value) => !Number.isFinite(value))) {
    throw new Error("PCHIP inputs must be finite, aligned, and strictly increasing");
  }
  const widths = xs.slice(1).map((right, index) => right - xs[index]!);
  const slopes = widths.map((width, index) => (values[index + 1]! - values[index]!) / width);
  const derivatives = new Array<number>(xs.length);
  if (xs.length === 2) derivatives.fill(slopes[0]!);
  else {
    derivatives[0] = endpoint(widths[0]!, widths[1]!, slopes[0]!, slopes[1]!);
    derivatives[xs.length - 1] = endpoint(
      widths[widths.length - 1]!, widths[widths.length - 2]!,
      slopes[slopes.length - 1]!, slopes[slopes.length - 2]!,
    );
    for (let index = 1; index < xs.length - 1; index += 1) {
      const before = slopes[index - 1]!, after = slopes[index]!;
      if (before === 0 || after === 0 || Math.sign(before) !== Math.sign(after)) {
        derivatives[index] = 0;
      } else {
        const beforeWidth = widths[index - 1]!, afterWidth = widths[index]!;
        const firstWeight = 2 * afterWidth + beforeWidth;
        const secondWeight = afterWidth + 2 * beforeWidth;
        derivatives[index] = (firstWeight + secondWeight)
          / (firstWeight / before + secondWeight / after);
      }
    }
  }
  return { derivatives, values: [...values], widths, xs: [...xs] };
}

export function evaluateLoomPchip(table: LoomPchipTable, x: number): number {
  const { derivatives, values, widths, xs } = table;
  if (!Number.isFinite(x) || x < xs[0]! || x > xs[xs.length - 1]!) return Number.NaN;
  let low = 0, high = xs.length - 1;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (x < xs[middle]!) high = middle;
    else low = middle;
  }
  const segment = Math.min(xs.length - 2, low);
  const width = widths[segment]!;
  const t = (x - xs[segment]!) / width;
  const h00 = 2 * t ** 3 - 3 * t ** 2 + 1;
  const h10 = t ** 3 - 2 * t ** 2 + t;
  const h01 = -2 * t ** 3 + 3 * t ** 2;
  const h11 = t ** 3 - t ** 2;
  return h00 * values[segment]! + h10 * width * derivatives[segment]!
    + h01 * values[segment + 1]! + h11 * width * derivatives[segment + 1]!;
}
